<template>
  <div class="chat-widget">
    <button
      v-if="!open"
      class="chat-launcher"
      aria-label="Open chat"
      @click="openChat"
    >
      <ChatIcon />
      <span v-if="unread" class="chat-badge">{{ unread }}</span>
    </button>

    <section v-else class="chat-panel" aria-label="Customer support chat">
      <header class="chat-header">
        <div>
          <h2>Support Chat</h2>
          <span>{{ loading ? "Connecting..." : "Online support" }}</span>
        </div>
        <button aria-label="Close chat" @click="open = false">×</button>
      </header>

      <form
        v-if="!emailConfirmed"
        class="chat-email-form"
        @submit.prevent="startChat"
      >
        <div class="chat-welcome">
          <p>Hi! Welcome to ELEPHAS Customer Support 😊</p>
          <p>
            We’re here to help you with any questions or product support
            needs.<br />How can we assist you today?
          </p>
          <p>
            ① Product Issue<br />② Missing Accessories<br />③ Warranty
            Activation<br />④ Replacement Request<br />⑤ Other Questions
          </p>
          <p>
            Please leave your email address and a brief description of your
            issue below.<br />Our support team will reply within 24 hours.
          </p>
          <p>
            📧 Email:
            <a href="mailto:after-service@elephas.com.hk"
              >after-service@elephas.com.hk</a
            ><br />☎ Toll-Free Support:<br />US:
            <a href="tel:+18555220987">+1 855 522 0987</a><br />(Monday–Friday,
            9:00 AM–5:00 PM Pacific Time)
          </p>
          <p>
            Thank you for contacting ELEPHAS. We look forward to assisting you!
          </p>
        </div>
        <label for="chat-email">Enter your email to start a chat</label>
        <input
          id="chat-email"
          v-model.trim="email"
          type="email"
          autocomplete="email"
          placeholder="Email address"
          required
          :disabled="loading"
        />
        <button type="submit" :disabled="loading">Start chat</button>
      </form>

      <template v-else>
        <div ref="messages" class="chat-messages">
          <div class="chat-welcome chat-welcome-message">
            <p>Hi! Welcome to ELEPHAS Customer Support 😊</p>
            <p>
              We’re here to help you with any questions or product support
              needs.<br />How can we assist you today?
            </p>
            <p>
              ① Product Issue<br />② Missing Accessories<br />③ Warranty
              Activation<br />④ Replacement Request<br />⑤ Other Questions
            </p>
            <p>
              Please leave your email address and a brief description of your
              issue below.<br />Our support team will reply within 24 hours.
            </p>
            <p>
              📧 Email:
              <a href="mailto:after-service@elephas.com.hk">after-service@elephas.com.hk</a
              ><br />☎ Toll-Free Support:<br />US:
              <a href="tel:+18555220987">+1 855 522 0987</a
              ><br />(Monday–Friday, 9:00 AM–5:00 PM Pacific Time)
            </p>
            <p>
              Thank you for contacting ELEPHAS. We look forward to assisting
              you!
            </p>
          </div>
          <p v-if="!messages.length && !loading" class="chat-empty">
            Leave us a message and we will get back to you soon.
          </p>
          <div
            v-for="message in messages"
            :key="message.id"
            :class="['chat-message', message.sender]"
          >
            <span class="chat-avatar" aria-label="avatar"
              ><svg viewBox="0 0 24 24" aria-hidden="true">
                <circle cx="12" cy="8" r="4" />
                <path d="M4 21c.7-4 3.4-6 8-6s7.3 2 8 6" /></svg
            ></span>
            <div class="chat-message-content">
              <div class="chat-bubble">
                <img
                  v-if="message.message_type === 'image'"
                  :src="message.content"
                  alt="Uploaded image"
                  @load="scrollToLatest"
                  @click="openImage(message.content)"
                />
                <span v-else v-html="linkify(message.content)"></span>
              </div>
              <time class="chat-time">{{
                formatMessageTime(message.created_at)
              }}</time>
            </div>
          </div>
        </div>

        <form class="chat-composer" @submit.prevent="sendText">
          <input
            ref="file"
            type="file"
            accept="image/jpeg,image/png,image/webp"
            class="hidden"
            @change="sendImage"
          />
          <button
            type="button"
            aria-label="Send image"
            @click="$refs.file.click()"
          >
            ＋
          </button>
          <input
            v-model="draft"
            maxlength="200"
            placeholder="Type a message..."
            :disabled="sending"
          />
          <button type="submit" :disabled="sending || !draft.trim()">
            Send
          </button>
        </form>
      </template>
    </section>
  </div>
</template>

<script>
import request from "@/utils/request";
import linkify from "@/utils/linkify";
import { ChatIcon } from "@/components/icons/";

const visitorKey = "elephas_chat_visitor_id";
const emailKey = "elephas_chat_email";

function visitorId() {
  let value = localStorage.getItem(visitorKey);
  if (!value) {
    value =
      window.crypto && window.crypto.randomUUID
        ? window.crypto.randomUUID()
        : "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
            const r = (Math.random() * 16) | 0;
            return (c === "x" ? r : (r & 3) | 8).toString(16);
          });
    localStorage.setItem(visitorKey, value);
  }
  return value;
}

export default {
  name: "ChatWidget",
  components: { ChatIcon },
  data() {
    return {
      open: false,
      loading: false,
      sending: false,
      email: localStorage.getItem(emailKey) || "",
      emailConfirmed: Boolean(localStorage.getItem(emailKey)),
      draft: "",
      messages: [],
      unread: 0,
      session: null,
      poller: null,
    };
  },
  beforeDestroy() {
    clearInterval(this.poller);
  },
  methods: {
    linkify,
    formatMessageTime(value) {
      const date = new Date(value);
      return Number.isNaN(date.getTime())
        ? ""
        : date.toLocaleString([], {
            month: "2-digit",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
            hour12: false,
          });
    },
    scrollToLatest() {
      this.$nextTick(() =>
        requestAnimationFrame(() => {
          if (this.open && this.$refs.messages)
            this.$refs.messages.scrollTop = this.$refs.messages.scrollHeight;
        }),
      );
    },
    async openChat() {
      this.open = true;
      this.unread = 0;
      if (this.emailConfirmed && !this.session) await this.startSession();
    },
    async startChat() {
      if (!this.email || this.loading) return;
      await this.startSession();
      localStorage.setItem(emailKey, this.email);
      this.emailConfirmed = true;
    },
    async startSession() {
      this.loading = true;
      try {
        const result = await request.post("/v1/chat/session", {
          visitor_id: visitorId(),
          email: this.email,
        });
        this.session = result.data;
        await this.loadMessages();
        this.poller = setInterval(() => this.loadMessages(true), 4000);
      } finally {
        this.loading = false;
      }
    },
    async loadMessages(silent = false) {
      if (!this.session) return;
      let result;
      try {
        result = await request.get("/v1/chat/messages", {
          params: { visitor_id: visitorId(), page: 1, size: 100 },
          silent,
        });
      } catch (error) {
        if (silent) return;
        throw error;
      }
      const incoming = (result.data || []).slice().reverse();
      const previousLast =
        this.messages.length && this.messages[this.messages.length - 1].id;
      this.messages = incoming;
      if (
        !this.open &&
        previousLast &&
        incoming.length &&
        incoming[incoming.length - 1].id !== previousLast
      ) {
        this.unread += 1;
      }
      this.scrollToLatest();
    },
    async sendText() {
      const content = this.draft.trim();
      if (!content || !this.session || this.sending) return;
      this.sending = true;
      try {
        const result = await request.post("/v1/chat/messages", {
          visitor_id: visitorId(),
          content,
        });
        this.messages.push(result.data);
        this.draft = "";
        this.scrollToLatest();
      } finally {
        this.sending = false;
      }
    },
    async sendImage(event) {
      const image = event.target.files[0];
      event.target.value = "";
      if (!image || !this.session || image.size > 10 * 1024 * 1024) return;
      const compressed = await this.compressImage(image);
      const data = new FormData();
      data.append("visitor_id", visitorId());
      data.append("image", compressed, image.name);
      this.sending = true;
      try {
        const result = await request.post("/v1/chat/messages/image", data, {
          isFormData: true,
        });
        this.messages.push(result.data);
        this.scrollToLatest();
      } finally {
        this.sending = false;
      }
    },
    openImage(url) {
      window.open(url, "_blank");
    },
    compressImage(file) {
      return new Promise((resolve) => {
        const image = new Image();
        image.onload = () => {
          const scale = Math.min(1, 1600 / Math.max(image.width, image.height));
          const canvas = document.createElement("canvas");
          canvas.width = Math.round(image.width * scale);
          canvas.height = Math.round(image.height * scale);
          canvas
            .getContext("2d")
            .drawImage(image, 0, 0, canvas.width, canvas.height);
          canvas.toBlob(
            (blob) => resolve(blob || file),
            file.type === "image/png" ? "image/png" : "image/jpeg",
            0.8,
          );
        };
        image.src = URL.createObjectURL(file);
      });
    },
  },
};
</script>

<style scoped>
.chat-widget {
  position: fixed;
  right: 24px;
  bottom: 22%;
  z-index: 60;
}
.chat-launcher {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  color: #fff;
  background: #2f63f6;
  border-radius: 9999px;
  box-shadow: 0 8px 24px rgba(47, 99, 246, 0.28);
}
.chat-badge {
  position: absolute;
  top: -4px;
  right: -4px;
  min-width: 20px;
  padding: 2px 5px;
  color: #fff;
  background: #182230;
  border-radius: 9999px;
  font-size: 12px;
}
.chat-panel {
  display: flex;
  flex-direction: column;
  width: 480px;
  height: 520px;
  overflow: hidden;
  background: #fff;
  border: 1px solid #e4eaf2;
  border-radius: 14px;
  box-shadow: 0 16px 40px rgba(31, 52, 84, 0.16);
}
.chat-header {
  display: flex;
  justify-content: space-between;
  padding: 16px;
  color: #182230;
  background: #fff;
  border-bottom: 1px solid #e4eaf2;
}
.chat-header h2 {
  margin: 0;
  font-size: 14px;
}
.chat-header span {
  font-size: 11px;
  opacity: 0.75;
}
.chat-header button {
  color: #8793a5;
  font-size: 24px;
  line-height: 1;
}
.chat-email-form {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 10px;
  min-height: 0;
  padding: 20px 24px;
}
.chat-welcome {
  flex: 1;
  overflow-y: auto;
  color: #4b5563;
  font-size: 13px;
  line-height: 1.5;
}
.chat-welcome p {
  margin: 0 0 12px;
}
.chat-welcome a {
  color: #2f63f6;
  text-decoration: underline;
}
.chat-welcome-message {
  flex: none;
  margin-bottom: 16px;
  padding: 12px;
  background: #fff;
  border: 1px solid #e4eaf2;
  border-radius: 8px;
}
.chat-email-form label {
  color: #1f2937;
  font-size: 14px;
  font-weight: 600;
}
.chat-email-form input {
  padding: 10px 12px;
  border: 1px solid #d8dee8;
  border-radius: 6px;
  outline: 0;
  font-size: 14px;
}
.chat-email-form input:focus {
  border-color: #2f63f6;
}
.chat-email-form button {
  align-self: flex-end;
  min-width: 96px;
  padding: 9px 12px;
  color: #fff;
  background: #2f63f6;
  border-radius: 6px;
  font-size: 14px;
}
.chat-messages {
  flex: 1;
  padding: 20px 24px;
  overflow-y: auto;
  background: #f5f7fb;
}
.chat-empty {
  color: #8793a5;
  font-size: 12px;
  text-align: center;
}
.chat-message {
  display: flex;
  max-width: 76%;
  gap: 8px;
  margin: 8px 0;
}
.chat-message.visitor {
  flex-direction: row-reverse;
  margin-left: auto;
}
.chat-avatar {
  display: flex;
  flex: 0 0 30px;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  color: #fff;
  background: #cfd2d6;
  border-radius: 50%;
}
.chat-avatar svg {
  width: 17px;
  height: 17px;
  fill: none;
  stroke: currentColor;
  stroke-linecap: round;
  stroke-linejoin: round;
  stroke-width: 1.8;
}
.chat-message.agent .chat-avatar {
  background: #2f63f6;
}
.chat-message-content {
  min-width: 0;
}
.chat-bubble {
  padding: 8px 11px;
  color: #1f2937;
  background: #fff;
  border: 1px solid #edf0f4;
  border-radius: 14px;
  box-shadow: 0 1px 2px rgba(31, 35, 41, 0.06);
  font-size: 13px;
  white-space: pre-wrap;
  word-break: break-word;
}
.chat-bubble img {
  display: block;
  max-width: 240px;
  max-height: 240px;
  border-radius: 8px;
}
.chat-bubble :deep(a) {
  color: #2f63f6;
  text-decoration: underline;
}
.chat-time {
  display: block;
  margin-top: 3px;
  color: #8793a5;
  font-size: 11px;
}
.chat-message.visitor .chat-time {
  text-align: right;
}
.chat-composer {
  display: flex;
  gap: 8px;
  padding: 10px;
  border-top: 1px solid #e4eaf2;
  font-size: 13px;
}
.chat-composer input:not([type="file"]) {
  min-width: 0;
  flex: 1;
  padding: 8px;
  border: 0;
  outline: 0;
  font-size: 13px;
}
.chat-composer button {
  color: #2f63f6;
  font-size: 13px;
  white-space: nowrap;
}
.chat-composer button:last-child {
  min-width: 58px;
  padding: 0 10px;
  color: #fff;
  background: #2f63f6;
  border-radius: 6px;
}
@media (max-width: 480px) {
  .chat-widget {
    right: 12px;
    bottom: calc(12px + env(safe-area-inset-bottom));
  }
  .chat-panel {
    width: calc(100vw - 24px);
    height: min(520px, calc(100dvh - 24px - env(safe-area-inset-bottom)));
  }
}
</style>
